import { createContext, useEffect, useState, type FC, type PropsWithChildren } from "react";
import { type RunnerMainService } from "@/ipc";
import type { AppMainInfo, AppMainService } from "frontend-shared/local-runner";

export type DesktopContextValue = {
    isDesktop: boolean;
    runnerService?: RunnerMainService;
    appMainService?: AppMainService;
    appMainInfo?: AppMainInfo;
    isFullscreen?: boolean;
};

/**
 * This context can be used to check whether the dashboard is running in a desktop environment, and if so,
 * provides access to the {@link runnerMainService} IPC service.
 */
export const DesktopContext = createContext<DesktopContextValue | null>(null);

export const DesktopProvider: FC<
    {
        isDesktop: boolean;
        runnerService?: RunnerMainService;
        appMainService?: AppMainService;
    } & PropsWithChildren
> = ({ isDesktop, runnerService, appMainService, children }) => {
    const [appMainInfo, setAppMainInfo] = useState<AppMainInfo | undefined>(undefined);
    const [isFullscreen, setIsFullscreen] = useState<boolean>(false);

    useEffect(() => {
        if (!appMainService) {
            return;
        }
        appMainService.about().then(
            (appMainInfo: AppMainInfo) => {
                setAppMainInfo(appMainInfo);
            },
            (error: unknown) => {
                console.error("Failed to fetch app main info", error);
            },
        );
    }, [appMainService]);

    useEffect(() => {
        if (!appMainService) {
            return;
        }

        // before folks update the desktop app, the callback will throw an error
        try {
            const unsubscribe = appMainService.onFullscreenChange((isFullScreen: boolean) => {
                setIsFullscreen(isFullScreen);
            });

            return () => {
                unsubscribe();
            };
        } catch (error) {
            console.error("Failed to subscribe to fullscreen change", error);
        }
    }, [appMainService]);

    const value: DesktopContextValue = { isDesktop, runnerService, appMainService, appMainInfo, isFullscreen };
    return <DesktopContext.Provider value={value}>{children}</DesktopContext.Provider>;
};
